import type { NextWebVitalsMetric } from 'next/app';
import { type Device } from '../types';
import { logger } from '../logger/logger';

type Rating = 'good' | 'needs-improvement' | 'poor';

type INPMetric = {
  id: string;
  /**
   * Expected to be 'INP'.
   */
  name: 'INP';
  /**
   * timestamp is of type BigQueryTimestamp and will be added at the backend
   */
  timestamp: any | null;
  /**
   * Value of the metric. In the 'INP' case, this is the time from the interaction to the next possible paint.
   */
  value: number;
  /**
   * The time in milliseconds between the interaction and the next possible paint.
   * This is the same value as `value` but with a higher precision.
   */
  delta: number;
  /**
   * Text rating of the INP value like 'good', 'needs-improvement' or 'poor'.
   */
  rating: Rating;
  /**
   * The pathname of the page where the interaction occurred.
   */
  pathname: string;
  /**
   * The type of interaction.
   * For "pointerdown", "pointerup", or "click" events this will be "pointer",
   * and for "keydown" or "keyup" events this will be "keyboard".
   */
  interactionType: string | null;
  /**
   * A selector identifying the element that the user first interacted with
   * as part of the frame where the INP candidate interaction occurred.
   * If this value is an empty string, that generally means the element was
   * removed from the DOM after the interaction.
   */
  target: string | null;
  /**
   * The type of navigation that triggered the INP candidate.
   * This is one of the following values:
   * - `navigate`
   * - `reload`
   * - `back-forward`
   * - `prerender`
   * - `restore`
   */
  navigationType: string | null;
  /**
   * The user agent string of the browser that generated the INP candidate.
   * This is the same value as `navigator.userAgent`.
   */
  userAgent: string;
  /**
   * The device type of the browser that generated the INP candidate.
   * This is one of the following values:
   * - `desktop`
   * - `mobile`
   * - `tablet`
   */
  deviceType: Device | '';
  /**
   * The connection type of the browser that generated the INP candidate.
   * This is one of the following values:
   * - `wifi`
   * - `cellular`
   * - `none`
   * - `unknown`
   */
  connection: string | null;
  /**
   * Page type of the page where the interaction occurred.
   */
  pageType: string;
  /**
   * The loading state of the document at the time when the interaction
   * corresponding to INP occurred. If the
   * interaction occurred while the document was loading and executing script
   * (e.g. usually in the `dom-interactive` phase) it can result in long delays.
   * This value is similar to `document.readyState` but it subdivides the "interactive"
   * state into the time before and after the DOMContentLoaded event fires.
   *
   * State descriptions:
   * - `loading`: the initial document response has not yet been fully downloaded
   *   and parsed. This is equivalent to the corresponding `readyState` value.
   * - `dom-interactive`: the document has been fully loaded and parsed, but
   *   scripts may not have yet finished loading and executing.
   * - `dom-content-loaded`: the document is fully loaded and parsed, and all
   *   scripts (except `async` scripts) have loaded and finished executing.
   * - `complete`: the document and all of its sub-resources have finished
   *   loading. This is equivalent to the corresponding `readyState` value.
   */
  loadState: string | null;
  /**
   * The environment in which the INP candidate was generated.
   * This is one of the following values:
   * - `production`
   * - `staging`
   * - `development`
   */
  environment: string | null;
};

/** Helper function to report web-vitals to an analytics api
 * See here for more info: https://nextjs.org/docs/advanced-features/measuring-performance */
const sendWebVitals = (metric: NextWebVitalsMetric, detecetedDevice?: Device) => {
  // logger.info(metric);
  const connection =
    'connection' in navigator ? (navigator.connection as Record<string, any>) : null;
  const connectionType = connection && 'type' in connection ? (connection.type as string) : null;

  if (metric.name === 'INP') {
    // if (metric.value > 200) {
    // load and send only if the value is greater than 200ms
    const metricData: INPMetric = {
      id: metric.id,
      name: metric.name,
      timestamp: null,
      value: metric.value,
      delta: 'delta' in metric ? (metric.delta as number) : 0,
      rating:
        // eslint-disable-next-line no-nested-ternary
        'rating' in metric
          ? (metric.rating as Rating)
          : metric.value < 200
            ? 'good'
            : 'needs-improvement',
      pathname: window.location.href,
      interactionType: 'entries' in metric ? (metric.entries as any[])[0]?.name : '',
      // interactionType: metric.attribution?.eventType ? (metric.attribution.eventType as string) : null,
      target: 'entries' in metric ? (metric.entries as any[])[0]?.target?.outerHTML : null,
      // unfortunately, attribution does not work properly, so set the target from entries for now
      // target: metric.attribution?.eventTarget ? (metric.attribution.eventTarget as string) : null,
      navigationType: 'navigationType' in metric ? (metric.navigationType as string) : null,
      userAgent: navigator.userAgent,
      pageType: 'pagetype' in window ? `${window.pagetype}` : 'unknown',
      connection: connectionType,
      deviceType: detecetedDevice ?? '',
      loadState: metric.attribution?.loadState ? (metric.attribution.loadState as string) : null,
      environment: process.env.NEXT_PUBLIC_ENVIRONMENT ?? null,
    };
    // Use sendBeacon if available, otherwise use fetch
    const url = '/api/vitals';
    const blob = new Blob([JSON.stringify(metricData)], { type: 'application/json' });

    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, blob);
    } else {
      fetch(url, {
        body: JSON.stringify(metricData),
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }).catch(logger.error);
    }
    // }
  }
};

export { sendWebVitals };
